import * as React from 'react';
import { createBrowserRouter } from 'react-router-dom';
import { AppShell } from '@/components/layout/AppShell';
import { LoadingSkeleton } from '@taskall/ui';
import { NotFoundPage } from './NotFoundPage';
import { FullPageLoader, HomeRedirect, RequireAnonymous, RequireAuth, RequirePermission } from './guards';
import { RouteErrorBoundary } from './RouteErrorBoundary';

const lazy = <T extends Record<string, React.ComponentType>>(loader: () => Promise<T>, name: keyof T) =>
  React.lazy(async () => ({ default: (await loader())[name] as React.ComponentType }));

const LoginPage = lazy(() => import('@/features/auth/LoginPage'), 'LoginPage');
const OnboardingPage = lazy(() => import('@/features/onboarding/OnboardingPage'), 'OnboardingPage');
const DashboardPage = lazy(() => import('@/features/dashboard/DashboardPage'), 'DashboardPage');
const ClassesPage = lazy(() => import('@/features/classes/ClassesPage'), 'ClassesPage');
const ClassDetailPage = lazy(() => import('@/features/classes/ClassDetailPage'), 'ClassDetailPage');
const CreateClassPage = lazy(() => import('@/features/classes/CreateClassPage'), 'CreateClassPage');
const StudentsPage = lazy(() => import('@/features/students/StudentsPage'), 'StudentsPage');
const StudentProfilePage = lazy(() => import('@/features/students/StudentProfilePage'), 'StudentProfilePage');
const StudentFormPage = lazy(() => import('@/features/students/StudentFormPage'), 'StudentFormPage');
const SchoolsPage = lazy(() => import('@/features/schools/SchoolsPage'), 'SchoolsPage');
const CreateSchoolPage = lazy(() => import('@/features/schools/CreateSchoolPage'), 'CreateSchoolPage');
const SchoolDetailPage = lazy(() => import('@/features/schools/SchoolDetailPage'), 'SchoolDetailPage');
const CalendarPage = lazy(() => import('@/features/calendar/CalendarPage'), 'CalendarPage');
const ReportsPage = lazy(() => import('@/features/reports/ReportsPage'), 'ReportsPage');
const UsersPage = lazy(() => import('@/features/users/UsersPage'), 'UsersPage');
const RequestsPage = lazy(() => import('@/features/ouvidoria/RequestsPage'), 'RequestsPage');
const KnowledgeBasePage = lazy(() => import('@/features/ouvidoria/KnowledgeBasePage'), 'KnowledgeBasePage');
const ChangePasswordPage = lazy(() => import('@/features/auth/ChangePasswordPage'), 'ChangePasswordPage');
const PlatformAnalyticsPage = lazy(() => import('@/features/platform/PlatformAnalyticsPage'), 'PlatformAnalyticsPage');
const PlatformHealthPage = lazy(() => import('@/features/platform/PlatformHealthPage'), 'PlatformHealthPage');

const page = (el: React.ReactNode) => <React.Suspense fallback={<LoadingSkeleton rows={6} />}>{el}</React.Suspense>;
const guarded = (permission: Parameters<typeof RequirePermission>[0]['permission'], el: React.ReactNode) =>
  page(<RequirePermission permission={permission}>{el}</RequirePermission>);

export const router = createBrowserRouter([
  {
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        element: <RequireAnonymous />,
        children: [{ path: '/login', element: <React.Suspense fallback={<FullPageLoader />}><LoginPage /></React.Suspense> }],
      },
      {
        element: <RequireAuth />,
        children: [
          { path: '/onboarding', element: <React.Suspense fallback={<FullPageLoader />}><OnboardingPage /></React.Suspense> },
          // Primeiro acesso: troca obrigatória da senha provisória (fora do layout).
          { path: '/trocar-senha', element: <React.Suspense fallback={<FullPageLoader />}><ChangePasswordPage /></React.Suspense> },
          {
            element: <AppShell />,
            children: [
              { index: true, element: <HomeRedirect /> },
              { path: '/dashboard', element: guarded('dashboard:read', <DashboardPage />) },
              { path: '/turmas', element: guarded('class:read', <ClassesPage />) },
              { path: '/turmas/nova', element: guarded('class:write', <CreateClassPage />) },
              { path: '/turmas/:classId', element: guarded('class:read', <ClassDetailPage />) },
              { path: '/alunos', element: guarded('student:read', <StudentsPage />) },
              { path: '/alunos/novo', element: guarded('student:write', <StudentFormPage />) },
              { path: '/alunos/:studentId', element: guarded('student:read', <StudentProfilePage />) },
              { path: '/alunos/:studentId/editar', element: guarded('student:write', <StudentFormPage />) },
              { path: '/escolas', element: guarded('school:read', <SchoolsPage />) },
              { path: '/escolas/nova', element: guarded('school:create', <CreateSchoolPage />) },
              { path: '/escolas/:schoolId', element: guarded('school:read', <SchoolDetailPage />) },
              { path: '/agenda', element: guarded('event:read', <CalendarPage />) },
              { path: '/relatorios', element: guarded('report:read', <ReportsPage />) },
              { path: '/usuarios', element: guarded('user:manage', <UsersPage />) },
              { path: '/solicitacoes', element: guarded('ouvidoria:read', <RequestsPage />) },
              { path: '/bases-de-conhecimento', element: guarded('knowledge:manage', <KnowledgeBasePage />) },
              { path: '/alterar-senha', element: page(<ChangePasswordPage />) },
              { path: '/plataforma', element: guarded('platform:analytics', <PlatformAnalyticsPage />) },
              { path: '/plataforma/saude', element: guarded('platform:health', <PlatformHealthPage />) },
              { path: '*', element: <NotFoundPage /> },
            ],
          },
        ],
      },
    ],
  },
]);
